import React, { useState, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useKanban } from '../context/KanbanContext';
import { useAuth } from '../context/AuthContext';
import { ModuloSistema } from '../types';
import { FormularioRegistro } from './FormularioRegistro';
import { MetricasPanel } from './MetricasPanel';
import { BacklogPanel } from './BacklogPanel';
import { PoliticasPanel } from './PoliticasPanel';
import { WipLimitesPanel } from './WipLimitesPanel';
import { ClientesPanel } from './ClientesPanel';
import { UsuariosPanel } from './UsuariosPanel';

interface HeaderProps {
    onRefresh: () => void;
    loading: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onRefresh, loading }) => {
    const { swimlanes, backlogAntiguo } = useKanban();
    const { usuario } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [showMetricas, setShowMetricas] = useState(false);
    const [showBacklog, setShowBacklog] = useState(false);
    const [showPoliticas, setShowPoliticas] = useState(false);
    const [showWipLimites, setShowWipLimites] = useState(false);
    const [showClientes, setShowClientes] = useState(false);
    const [showUsuarios, setShowUsuarios] = useState(false);
    const [showModulosMenu, setShowModulosMenu] = useState(false);

    // Mismo criterio que requirePermiso en el backend: Gerente ve todo.
    const puede = (modulo: ModuloSistema): boolean =>
        usuario?.rol === 'gerente' || Boolean(usuario?.permisos_modulos?.includes(modulo));

    const metrics = useMemo(() => {
        let total = 0;
        let completadas = 0;
        let enProgreso = 0;
        let bloqueadas = 0;

        swimlanes.forEach(swimlane => {
            Object.values(swimlane.tareas).forEach(tareas => {
                tareas.forEach(tarea => {
                    total++;
                    if (tarea.estado === 'produccion') completadas++;
                    if (tarea.estado === 'en_progreso' || tarea.estado === 'esperando_cliente') enProgreso++;
                    if (tarea.bloqueado) bloqueadas++;
                });
            });
        });

        return { total, completadas, enProgreso, bloqueadas };
    }, [swimlanes]);

    const backlogAntiguoCount = useMemo(
        () => backlogAntiguo.filter(t => t.dias_en_backlog > 90).length,
        [backlogAntiguo]
    );

    return (
        <>
            <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-xl">
                <div className="max-w-full mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2.5 rounded-xl shadow-lg">
                                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-xl font-bold tracking-tight">Minotion</h1>
                                <p className="text-xs text-slate-400">Gestión de Proyectos y Soporte</p>
                            </div>
                        </div>

                        <div className="hidden lg:flex items-center gap-6">
                            <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                                    <span className="text-slate-300">Total:</span>
                                    <span className="font-semibold">{metrics.total}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                    <span className="text-slate-300">Completadas:</span>
                                    <span className="font-semibold text-emerald-400">{metrics.completadas}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                    <span className="text-slate-300">En Progreso:</span>
                                    <span className="font-semibold text-amber-400">{metrics.enProgreso}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <span className="text-slate-300">Bloqueadas:</span>
                                    <span className="font-semibold text-red-400">{metrics.bloqueadas}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={onRefresh}
                                disabled={loading}
                                className="p-2.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all duration-200 disabled:opacity-50"
                                title="Actualizar"
                            >
                                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                            {puede('kanban') && (
                                <button
                                    onClick={() => setShowForm(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 font-medium"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span className="hidden sm:inline">Nueva Tarea</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="lg:hidden px-6 pb-4 overflow-x-auto">
                    <div className="flex items-center gap-4 text-xs w-max">
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                            <span className="text-slate-400">Total:</span>
                            <span className="font-semibold text-white">{metrics.total}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            <span className="text-slate-400">OK:</span>
                            <span className="font-semibold text-emerald-400">{metrics.completadas}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                            <span className="text-slate-400">Progreso:</span>
                            <span className="font-semibold text-amber-400">{metrics.enProgreso}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span className="text-slate-400">Bloqueadas:</span>
                            <span className="font-semibold text-red-400">{metrics.bloqueadas}</span>
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-700/50 px-6 py-2.5">
                    {/* Desktop/laptop (≥768px): fila de botones como siempre. */}
                    <div className="hidden md:flex items-center gap-2 flex-wrap">
                        {puede('kanban') && (
                            <button
                                onClick={() => setShowMetricas(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                            >
                                📊 Métricas
                            </button>
                        )}
                        {puede('kanban') && (
                            <button
                                onClick={() => setShowBacklog(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                            >
                                🗂️ Backlog
                                {backlogAntiguoCount > 0 && (
                                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                                        {backlogAntiguoCount}
                                    </span>
                                )}
                            </button>
                        )}
                        {puede('configuracion') && (
                            <button
                                onClick={() => setShowWipLimites(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                            >
                                ⚙️ Límites WIP
                            </button>
                        )}
                        {puede('configuracion') && (
                            <button
                                onClick={() => setShowPoliticas(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                            >
                                ℹ️ Políticas
                            </button>
                        )}
                        {puede('clientes') && (
                            <button
                                onClick={() => setShowClientes(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                            >
                                👥 Clientes
                            </button>
                        )}
                        {usuario?.rol === 'gerente' && (
                            <button
                                onClick={() => setShowUsuarios(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors"
                            >
                                🧑‍💼 Usuarios
                            </button>
                        )}
                    </div>

                    {/* Mobile (<768px): mismos accesos, colapsados detrás de un botón. */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setShowModulosMenu(v => !v)}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors w-full justify-between"
                            aria-expanded={showModulosMenu}
                        >
                            <span className="flex items-center gap-1.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                                Módulos
                            </span>
                            <svg className={`w-4 h-4 transition-transform ${showModulosMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        <AnimatePresence initial={false}>
                            {showModulosMenu && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="overflow-hidden"
                                >
                                    <div className="flex flex-col gap-1.5 pt-2">
                                        {puede('kanban') && (
                                            <button
                                                onClick={() => { setShowMetricas(true); setShowModulosMenu(false); }}
                                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                                            >
                                                📊 Métricas
                                            </button>
                                        )}
                                        {puede('kanban') && (
                                            <button
                                                onClick={() => { setShowBacklog(true); setShowModulosMenu(false); }}
                                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                                            >
                                                🗂️ Backlog
                                                {backlogAntiguoCount > 0 && (
                                                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                                                        {backlogAntiguoCount}
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                        {puede('configuracion') && (
                                            <button
                                                onClick={() => { setShowWipLimites(true); setShowModulosMenu(false); }}
                                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                                            >
                                                ⚙️ Límites WIP
                                            </button>
                                        )}
                                        {puede('configuracion') && (
                                            <button
                                                onClick={() => { setShowPoliticas(true); setShowModulosMenu(false); }}
                                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                                            >
                                                ℹ️ Políticas
                                            </button>
                                        )}
                                        {puede('clientes') && (
                                            <button
                                                onClick={() => { setShowClientes(true); setShowModulosMenu(false); }}
                                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                                            >
                                                👥 Clientes
                                            </button>
                                        )}
                                        {usuario?.rol === 'gerente' && (
                                            <button
                                                onClick={() => { setShowUsuarios(true); setShowModulosMenu(false); }}
                                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-slate-700/50 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                                            >
                                                🧑‍💼 Usuarios
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </header>

            {showForm && (
                <FormularioRegistro
                    onClose={() => setShowForm(false)}
                    onSuccess={onRefresh}
                />
            )}

            {showMetricas && (
                <MetricasPanel onClose={() => setShowMetricas(false)} />
            )}

            {showBacklog && (
                <BacklogPanel onClose={() => setShowBacklog(false)} />
            )}

            {showPoliticas && (
                <PoliticasPanel onClose={() => setShowPoliticas(false)} />
            )}

            {showWipLimites && (
                <WipLimitesPanel onClose={() => setShowWipLimites(false)} />
            )}

            {showClientes && (
                <ClientesPanel onClose={() => setShowClientes(false)} />
            )}

            {showUsuarios && usuario?.rol === 'gerente' && (
                <UsuariosPanel onClose={() => setShowUsuarios(false)} />
            )}
        </>
    );
};
